import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { History, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import type { TrackedJob, RunLogRow } from "./types";

interface Props {
  job: TrackedJob;
}

const RunHistoryPopover = ({ job }: Props) => {
  const [runs, setRuns] = useState<RunLogRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("maintenance_run_log")
      .select("function_name, affected_count, success, ran_at")
      .eq("function_name", job.functionName)
      .order("ran_at", { ascending: false })
      .limit(5);
    if (!error) setRuns((data as RunLogRow[] | null) ?? []);
    setLoading(false);
  }, [job.functionName]);

  return (
    <Popover onOpenChange={(open) => { if (open) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="px-2 border-l border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors flex items-center justify-center shrink-0"
          title="View last 5 runs"
          aria-label={`View last 5 runs for ${job.label}`}
        >
          <History size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="px-3 py-2 border-b border-border">
          <p className="text-xs font-medium">{job.label}</p>
          <p className="text-[10px] text-muted-foreground">Last 5 runs from log</p>
        </div>
        <div className="max-h-64 overflow-auto">
          {loading && !runs ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : (runs?.length ?? 0) === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              No runs logged yet
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {runs!.map((run, idx) => (
                <li key={`${run.ran_at}-${idx}`} className="px-3 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {run.success ? (
                      <CheckCircle2 size={12} className="text-primary shrink-0" />
                    ) : (
                      <XCircle size={12} className="text-destructive shrink-0" />
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="text-[11px] font-medium truncate" title={format(new Date(run.ran_at), "PPpp")}>
                        {format(new Date(run.ran_at), "MMM d, h:mm a")}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(run.ran_at))} ago
                      </span>
                    </div>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {run.affected_count} {job.countLabel}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RunHistoryPopover;
